import 'client-only';

import { logger } from '@curology/logger/client';
import { v4 } from 'uuid';

import { type Brand } from '@/consts/brands';
import COOKIE_KEYS from '@/consts/cookieKeys';
import cookies from '@/utils/cookies/client';
import { post } from '@/utils/http/client';
import { utmParams } from '@/utils/http/client/utmParams';
import { isDefined } from '@/utils/isDefined';
import { urls } from '@/utils/urls';

const hasValidUtmParams = () => {
  const { source, medium, name } = utmParams();

  return [source, medium, name].filter((utmParam) => !!utmParam).length > 1;
};

const hasValidReferrer = () => !!document.referrer;

// Must stay in lockstep with PocketDerm's UserReferrerUtm::REFERRER_ID_TYPES
// (app/Models/UserReferrerUtm.php). Keys are case-sensitive — the backend
// matches them via Collection::only() in UrlHelper::getReferrerIdAndType().
// If you add/remove an entry here, update the PocketDerm constant in the
// same change (and vice versa) — otherwise this trigger will fire POSTs
// the backend can't attribute, or paid clicks the backend recognizes will
// silently skip the referrer-utms capture.
//
// Why this trigger exists: paid/affiliate landings often have no UTMs and
// a stripped document.referrer (cross-domain navigations, privacy browsers),
// so the click ID is the only attribution signal we can rely on.
const REFERRER_ID_PARAMS = ['ranSiteID', 'gclid', 'ttclid'] as const;

const hasValidReferrerId = () => {
  const params = new URLSearchParams(window.location.search);
  return REFERRER_ID_PARAMS.some((key) => {
    const value = params.get(key);
    return !!value && value.trim().length > 0;
  });
};

const getUuid = () => {
  const queryParams = new URLSearchParams(window.location.search);
  if (queryParams.has('uuid')) {
    return queryParams.get('uuid');
  }

  const ldUuid = cookies.get(COOKIE_KEYS.ldUuid);
  if (isDefined(ldUuid)) {
    return ldUuid;
  }

  return v4();
};

const setUuid = () => {
  if (cookies.has(COOKIE_KEYS.curologyUuid)) return;

  const uuid = getUuid();
  const days = 1000;

  if (uuid) {
    cookies.set(COOKIE_KEYS.curologyUuid, uuid, { expires: days });
  }
};

export const reportReferrerUtm = (brand: Brand) => {
  setUuid();

  const curologyUuid = cookies.get(COOKIE_KEYS.curologyUuid);

  if (
    isDefined(curologyUuid) &&
    (hasValidUtmParams() || hasValidReferrer() || hasValidReferrerId())
  ) {
    post(urls[brand].api.referrerUtms.store, {
      uuid: curologyUuid,
      landing_page_url: window.location.href,
      referrer_url: document.referrer,
      user_agent: (!!window.navigator && window.navigator.userAgent) || '',
    }).catch((error) => {
      logger.log(`Failed to report UTM/Referrer: ${error}`);
    });
  }
};
